import type { Point, SceneNode } from "../scene/model";
import { transformPoint } from "../geometry/affine";
import { nodeMatrix } from "./transforms";
import { edgeExtrema, validEdgeWarp, zeroEdges } from "../geometry/edge-warp";
export const defaultQuad = (): Point[] => [
  { x: -0.5, y: -0.5 },
  { x: 0.5, y: -0.5 },
  { x: 0.5, y: 0.5 },
  { x: -0.5, y: 0.5 },
];
export function warpQuad(node: SceneNode) {
  return node.geometry.warpCorners ?? defaultQuad();
}
/** Unit square -> convex quadrilateral projective mapping. */
export function perspective(
  quad: readonly Point[],
  u: number,
  v: number,
): Point {
  const [a, b, c, d] = quad as [Point, Point, Point, Point];
  const dx1 = b.x - c.x,
    dx2 = d.x - c.x,
    dx3 = a.x - b.x + c.x - d.x,
    dy1 = b.y - c.y,
    dy2 = d.y - c.y,
    dy3 = a.y - b.y + c.y - d.y;
  const det = dx1 * dy2 - dx2 * dy1;
  const g = Math.abs(det) > 1e-12 ? (dx3 * dy2 - dx2 * dy3) / det : 0,
    h = Math.abs(det) > 1e-12 ? (dx1 * dy3 - dx3 * dy1) / det : 0;
  const z = g * u + h * v + 1;
  return {
    x: ((b.x - a.x + g * b.x) * u + (d.x - a.x + h * d.x) * v + a.x) / z,
    y: ((b.y - a.y + g * b.y) * u + (d.y - a.y + h * d.y) * v + a.y) / z,
  };
}
export function validQuad(q: readonly Point[]) {
  if (
    q.length !== 4 ||
    q.some(
      (p) => ![p.x, p.y].every((n) => Number.isFinite(n) && Math.abs(n) < 100),
    )
  )
    return false;
  const cross = q.map((a, i) => {
    const b = q[(i + 1) % 4]!,
      c = q[(i + 2) % 4]!;
    return (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
  });
  return cross.every((n) => n > 1e-5);
}
export function inversePerspective(q: readonly Point[], p: Point): Point {
  const [a, b, c, d] = q as [Point, Point, Point, Point];
  const dx1 = b.x - c.x,
    dx2 = d.x - c.x,
    dx3 = a.x - b.x + c.x - d.x,
    dy1 = b.y - c.y,
    dy2 = d.y - c.y,
    dy3 = a.y - b.y + c.y - d.y,
    det = dx1 * dy2 - dx2 * dy1;
  const g = (dx3 * dy2 - dx2 * dy3) / det,
    h = (dx1 * dy3 - dx3 * dy1) / det,
    A = b.x - a.x + g * b.x,
    B = d.x - a.x + h * d.x,
    C = a.x,
    D = b.y - a.y + g * b.y,
    E = d.y - a.y + h * d.y,
    F = a.y;
  const z = (D * h - E * g) * p.x + (B * g - A * h) * p.y + A * E - B * D;
  return {
    x: ((E - F * h) * p.x + (C * h - B) * p.y + B * F - C * E) / z,
    y: ((F * g - D) * p.x + (A - C * g) * p.y + C * D - A * F) / z,
  };
}
export function assertWarp(node: SceneNode) {
  if (
    node.geometry.warpEdges !== undefined &&
    (!node.geometry.warpCorners ||
      !validEdgeWarp(node.geometry.warpCorners, node.geometry.warpEdges))
  )
    throw new Error("Повреждён изгиб сторон объекта.");
  if (
    node.geometry.warpMapping !== undefined &&
    (node.kind !== "image" ||
      !node.geometry.warpCorners ||
      node.geometry.warpMapping !== "bilinear")
  )
    throw new Error("Неподдерживаемое преобразование изображения.");
  if (node.geometry.warpCorners && !validQuad(node.geometry.warpCorners))
    throw new Error("Повреждена геометрия деформации.");
  const size = node.geometry.warpSourceSize as
    { width: number; height: number } | undefined;
  if (
    size &&
    (!node.geometry.warpCorners ||
      ![size.width, size.height].every(
        (n) => Number.isFinite(n) && n >= 1 && n <= 1e6,
      ))
  )
    throw new Error("Повреждён исходный размер деформации.");
}
export function deformNode(node: SceneNode, quad: Point[]): SceneNode {
  if (!validQuad(quad)) return node;
  if (node.geometry.warpEdges && !validEdgeWarp(quad, node.geometry.warpEdges))
    return node;
  return {
    ...node,
    geometry: {
      ...node.geometry,
      warpCorners: quad,
      warpSourceSize: node.geometry.warpSourceSize ?? {
        width: node.geometry.width!,
        height: node.geometry.height!,
      },
    },
  };
}
/** Normalize the ordinary frame without moving any visible corner or losing the source. */
export function frameWarp(node: SceneNode): SceneNode {
  const q = warpQuad(node),
    extrema = edgeExtrema(q, node.geometry.warpEdges ?? zeroEdges()),
    xs = extrema.map((p) => p.x),
    ys = extrema.map((p) => p.y),
    left = Math.min(...xs),
    right = Math.max(...xs),
    top = Math.min(...ys),
    bottom = Math.max(...ys);
  const center = transformPoint(
    { x: (left + right) / 2, y: (top + bottom) / 2 },
    nodeMatrix(node),
  );
  return {
    ...node,
    transform: { ...node.transform, ...center },
    geometry: {
      ...node.geometry,
      width: node.geometry.width! * (right - left),
      height: node.geometry.height! * (bottom - top),
      warpCorners: q.map((p) => ({
        x: (p.x - left) / (right - left) - 0.5,
        y: (p.y - top) / (bottom - top) - 0.5,
      })),
      ...(node.geometry.warpEdges
        ? {
            warpEdges: node.geometry.warpEdges.map((p) => ({
              x: p.x / (right - left),
              y: p.y / (bottom - top),
            })),
          }
        : {}),
    },
  };
}
